import { sortBy } from "lodash";
import moment from "moment";
import { openBrowserAsync } from "expo-web-browser";

import { EligibilityStatus } from "./types";
import { RecommendedJob, PaySchedules } from "./JobsScreen/types";
import { Job } from "./JobDetailScreen/types";
import { Activity } from "./fragments";
import config from "../config";
import { Platform } from "react-native";

export const isEligible = (eligibilityStatus: EligibilityStatus | undefined) =>
  eligibilityStatus === EligibilityStatus.ELIGIBLE;

const paySchedules: PaySchedules = {
  Hourly: "hour",
  Salary: "year",
  Weekly: "week",
};

export const formatCurrency = ({
  value,
  unit,
  minimumFractionDigits = 2,
}: {
  value: number;
  unit: string;
  minimumFractionDigits?: number;
}) => new Intl.NumberFormat("en-US", { style: "currency", currency: unit, minimumFractionDigits }).format(value);

export function formatCompensation({
  base_compensation,
  base_compensation_max,
  pay_schedule,
  additional_income,
}: Pick<RecommendedJob, "base_compensation" | "base_compensation_max" | "pay_schedule" | "additional_income">) {
  const range = [base_compensation, base_compensation_max]
    .reduce<string[]>(
      (acc, compensation) => (compensation ? [...acc, formatCurrency({ ...compensation, minimumFractionDigits: 0 })] : acc),
      []
    )
    .join(" - ");

  const paySchedule = range && pay_schedule && paySchedules[pay_schedule];

  const rate = [range, paySchedule].filter(Boolean).join("/");

  const additionalIncome = rate && additional_income && "(+)";

  return [rate, additionalIncome].filter(Boolean).join(" ");
}

export enum ActivityUpdate {
  New,
  Unavailable,
  NameUpdated,
  DescriptionUpdated,
  ParticipationLevelUpdated,
}

const getParticipationLevelChanges = (activity: Activity) =>
  sortBy(activity.participationLevels, (p) => p.points)
    .map((p) => `${p.points}${p.requirements}`)
    .join("/");

export function getActivityUpdates(currentActivity?: Activity | null, oldActivity?: Activity | null) {
  const updates: ActivityUpdate[] = [];

  if (currentActivity && !oldActivity) {
    updates.push(ActivityUpdate.New);
  } else if (!currentActivity && oldActivity) {
    updates.push(ActivityUpdate.Unavailable);
  } else if (currentActivity && oldActivity) {
    if (currentActivity.name !== oldActivity.name) updates.push(ActivityUpdate.NameUpdated);
    if (currentActivity.description !== oldActivity.description) updates.push(ActivityUpdate.DescriptionUpdated);
    if (getParticipationLevelChanges(currentActivity) !== getParticipationLevelChanges(oldActivity)) {
      updates.push(ActivityUpdate.ParticipationLevelUpdated);
    }
  }

  return updates;
}

export function isActivityUpdatedOrUnavailable(activityUpdates: ActivityUpdate[]) {
  return activityUpdates.length > 0 && !activityUpdates.includes(ActivityUpdate.New);
}

export function getActivePreviousPlan(planTransition?: { previousPlan?: { submissionWindowClosesOn?: string | null } }) {
  return planTransition?.previousPlan?.submissionWindowClosesOn ? planTransition.previousPlan : undefined;
}

const encodeQueryParams = (params: { [key: string]: string | number | boolean }) =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

export const formatTotalCompensation = (job: Job) => formatCurrency({ ...job.total_compensation, minimumFractionDigits: 0 });

export const planStartYear = (plan: { startsOn: string }) => moment(plan.startsOn).year();
export const formatPlanDate = (date: string) => moment(date).utcOffset(0).format("MMMM D, YYYY");

export const openBrowser = (url: string, refetch?: () => void) => async () => {
  if (Platform.OS === "web") {
    window.open(url, "_blank");
  } else {
    await openBrowserAsync(url);
  }
  if (refetch) {
    refetch();
  }
};

export const getPlaceJson = (path: string, params: Record<string, string>) =>
  fetch(
    `${config.googleMapsApiUrl}/place/${path}/json?${encodeQueryParams({
      key: config.googlePlacesApiKeyUnrestricted,
      ...params,
    })}`,
    {
      method: "GET",
    }
  ).then((result) => result.json());

type ReverseGeocodeResult = { result: { city?: string; state?: string } };

export const reverseGeocode = ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<ReverseGeocodeResult> =>
  fetch(`${config.geocodeApiUrl}/reverse.json?${encodeQueryParams({ q: [latitude, longitude].join() })}`).then((result) =>
    result.json()
  );
